import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Button, Card, CardContent } from "@mui/material";
import { styled, keyframes } from "@mui/system";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { API_URL } from "./config";

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
`;

const Container = styled(Box)({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #0c0c0c 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)",
  padding: "1rem",
});

const StyledCard = styled(Card)({
  maxWidth: 460,
  width: "100%",
  borderRadius: 24,
  background: "rgba(255,255,255,0.06)",
  backdropFilter: "blur(20px)",
  border: "2px solid rgba(255,255,255,0.15)",
  boxShadow: "0 12px 48px rgba(0,0,0,0.4)",
  textAlign: "center",
  padding: "1rem",
});

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | success | waiting | error
  const [purchaseData, setPurchaseData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const MAX_ATTEMPTS = 10;

  // קריאת user_id + מזהה החבילה מ-localStorage (נשמרו לפני המעבר לתשלום)
  const savedUserId = localStorage.getItem("pending_payment_user");
  const savedPackageId = localStorage.getItem("pending_payment_package");

  useEffect(() => {
    // שליפת transactionid מ-URL (UPay שולח אותו כפרמטר)
    const transactionId =
      searchParams.get("transactionid") ||
      searchParams.get("transactionId") ||
      searchParams.get("transaction_id") ||
      null;

    const verify = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/verify_purchase`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // הטוקן אופציונלי — אם פג תוקף, השרת נופל ל-user_id שנשמר ב-localStorage
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            transactionId,
            user_id: savedUserId,
            packageId: savedPackageId || undefined,
          }),
        });
        const data = await res.json();

        if (data.found && data.success) {
          setPurchaseData(data);
          setStatus("success");
          // ניקוי localStorage אחרי הצלחה
          localStorage.removeItem("pending_payment_user");
          localStorage.removeItem("pending_payment_package");
        } else if (data.found && !data.success) {
          // עסקה נמצאה אבל נכשלה
          setStatus("error");
        } else {
          // עדיין לא נקלט — ננסה שוב
          setAttempts((prev) => prev + 1);
        }
      } catch {
        setAttempts((prev) => prev + 1);
      }
    };

    if (status === "loading" && attempts < MAX_ATTEMPTS) {
      const delay = attempts === 0 ? 500 : 3000;
      const timer = setTimeout(verify, delay);
      return () => clearTimeout(timer);
    }

    if (attempts >= MAX_ATTEMPTS && status === "loading") {
      setStatus("waiting");
    }
  }, [attempts, status, searchParams, savedUserId, savedPackageId]);

  const goToPanel = () => {
    const userId = purchaseData?.user_id || savedUserId;
    if (userId) {
      navigate(`/Panel/${userId}`);
    } else {
      navigate("/login");
    }
  };

  return (
    <Container dir="rtl">
      <StyledCard>
        <CardContent sx={{ py: 5, px: 3 }}>
          {status === "loading" && (
            <>
              <CircularProgress size={64} sx={{ color: "#818cf8", mb: 3 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", mb: 1 }}>
                מאמת את התשלום...
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: ".9rem" }}>
                ניסיון {attempts + 1} מתוך {MAX_ATTEMPTS}
              </Typography>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: "#4caf50",
                  mb: 2,
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#fff", mb: 1 }}>
                🎉 התשלום הצליח!
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", mb: 1, fontSize: "1rem" }}>
                החבילה נוספה לחשבון שלך
              </Typography>
              {purchaseData?.amount && (
                <Typography sx={{ color: "#818cf8", fontWeight: 700, fontSize: "1.2rem", mb: 3 }}>
                  ₪{purchaseData.amount}
                </Typography>
              )}
              <Button
                variant="contained"
                onClick={goToPanel}
                sx={{
                  py: 1.5,
                  px: 5,
                  borderRadius: 4,
                  fontWeight: 700,
                  fontSize: "1rem",
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  boxShadow: "0 6px 24px rgba(102,126,234,0.45)",
                  "&:hover": { boxShadow: "0 10px 32px rgba(102,126,234,0.55)" },
                }}
              >
                🎮 חזרה לפאנל
              </Button>
            </>
          )}

          {status === "waiting" && (
            <>
              <HourglassTopIcon
                sx={{ fontSize: 80, color: "#ff9800", mb: 2 }}
              />
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", mb: 1 }}>
                ממתין לאישור...
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.6)", mb: 3, fontSize: ".9rem", lineHeight: 1.8 }}>
                התשלום התקבל אבל העדכון טרם הגיע לשרת.
                <br />
                זה יכול לקחת מספר דקות. אם רכשת בהצלחה, החבילה תופיע בפאנל.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  onClick={() => { setStatus("loading"); setAttempts(0); }}
                  sx={{
                    py: 1.2, px: 4, borderRadius: 3, fontWeight: 700,
                    background: "linear-gradient(135deg, #ff9800, #f57c00)",
                  }}
                >
                  🔄 נסה שוב
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const userId = savedUserId;
                    if (userId) navigate(`/Panel/${userId}`);
                    else navigate("/login");
                  }}
                  sx={{
                    py: 1.2, px: 4, borderRadius: 3, fontWeight: 700,
                    color: "#fff", borderColor: "rgba(255,255,255,0.3)",
                    "&:hover": { borderColor: "#fff" },
                  }}
                >
                  חזרה לפאנל
                </Button>
              </Box>
            </>
          )}

          {status === "error" && (
            <>
              <ErrorOutlineIcon sx={{ fontSize: 80, color: "#f44336", mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", mb: 1 }}>
                שגיאה
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.6)", mb: 3 }}>
                לא הצלחנו לאמת את התשלום. נסה שוב או צור קשר.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  const userId = savedUserId;
                  if (userId) navigate(`/Panel/${userId}`);
                  else navigate("/login");
                }}
                sx={{
                  py: 1.2, px: 4, borderRadius: 3, fontWeight: 700,
                  color: "#fff", borderColor: "rgba(255,255,255,0.3)",
                }}
              >
                חזרה לפאנל
              </Button>
            </>
          )}
        </CardContent>
      </StyledCard>
    </Container>
  );
}
